import React, { useState, useEffect, useReducer } from 'react';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import logo from '../assets/logo.png';
import './Login.css';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaArrowRight } from 'react-icons/fa';

const Login = () => {
  // Force update hook
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });
  
  // Debug effect to log errors when they change
  useEffect(() => {
    console.log('Errors state updated:', JSON.stringify(errors, null, 2));
    
    // Force a re-render when errors change to ensure UI updates
    const timer = setTimeout(() => {
      console.log('Forcing re-render for errors');
      forceUpdate();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [errors]);
  const [loading, setLoading] = useState(false);
  const { login, logout, currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Toast notifications are now imported directly

  const from = location.state?.from?.pathname || '/';

  // Redirect already authenticated users
  useEffect(() => {
    if (currentUser) {
      console.log('User already authenticated, redirecting...');
      if (userRole === 'admin') {
        navigate('/admin');
      } else if (userRole === 'seller') {
        navigate('/seller/dashboard');
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [currentUser, userRole, navigate, from]);

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: '',
      general: ''
    };
    let isValid = true;

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission and propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
    
    // Prevent multiple submissions
    if (loading) {
      return false;
    }

    console.log('[Login] Form submission started');
    
    // Clear previous errors and set loading state
    setErrors({ email: '', password: '', general: '' });
    setLoading(true);
    
    // Clear any existing toasts
    toast.dismiss();
    
    // Validate form
    if (!validateForm()) {
      setLoading(false);
      return false;
    }
    
    // Add a timeout to handle cases where the login hangs
    const loginTimeout = setTimeout(() => {
      if (loading) {
        const timeoutMsg = 'Login is taking longer than expected. Please wait...';
        console.warn(timeoutMsg);
        toast.info(timeoutMsg);
      }
    }, 5000);

    try {
      console.log('[Login] Input validation passed');
      
      try {
        // Call login with email and password
        const user = await login(email, password);
        
        if (!user) {
          throw new Error('No user data returned from login');
        }
        
        
        console.log('Login successful, redirecting...');
        toast.success('Login successful! Redirecting...');
        
        // Clear the timeout since we're about to redirect
        clearTimeout(loginTimeout);
        
        // Redirect based on role
        setTimeout(() => {
          if (user.role === 'admin') {
            navigate('/admin');
          } else if (user.role === 'seller') {
            navigate('/seller/dashboard');
          } else {
            navigate(from, { replace: true });
          }
        }, 1000);
        
        return true;
        
      } catch (err) {
        console.error('Login error:', err);
        
        // Default error message
        let errorMessage = 'Failed to log in. Please check your credentials and try again.';
        let fieldErrors = {};
        
        // Handle field-specific errors from the auth service
        if (err.fieldErrors) {
          fieldErrors = { ...err.fieldErrors };
        }
        
        // Handle specific error cases
        if (err.response?.data) {
          const { data } = err.response;
          
          // Handle field-level errors
          if (data.email) {
            fieldErrors.email = Array.isArray(data.email) ? data.email[0] : data.email;
          }
          if (data.password) {
            fieldErrors.password = Array.isArray(data.password) ? data.password[0] : data.password;
          }
          if (data.non_field_errors) {
            errorMessage = Array.isArray(data.non_field_errors) 
              ? data.non_field_errors[0]
              : data.non_field_errors;
          } else if (data.detail) {
            errorMessage = data.detail;
          }
        } else if (err.message) {
          // Handle specific error messages
          if (err.message.includes('Invalid credentials') || 
              err.message.includes('No active account') ||
              err.message.includes('Incorrect email or password')) {
            errorMessage = 'Invalid email or password';
            fieldErrors.email = 'Invalid email or password';
            fieldErrors.password = 'Invalid email or password';
          } else if (err.message.includes('account is not verified')) {
            errorMessage = 'Please verify your email before logging in. Check your inbox for the verification link.';
          } else if (err.message.includes('too many attempts')) {
            errorMessage = 'Too many failed login attempts. Please try again later.';
          } else {
            errorMessage = err.message;
          }
        }
        
        // Update error state
        const newErrors = {
          ...fieldErrors,
          general: Object.keys(fieldErrors).length === 0 ? errorMessage : ''
        };
        
        console.log('Setting errors:', JSON.stringify(newErrors, null, 2));
        setErrors(newErrors);
        
        // Show toast for general errors
        if (newErrors.general) {
          toast.error(newErrors.general);
        } else if (Object.keys(fieldErrors).length > 0) {
          toast.error('Please check the form for errors');
        }
        
        setLoading(false);
        clearTimeout(loginTimeout);
        return false;
      }
    } catch (err) {
      console.error('[Login] Unexpected error in login process:', err);
      
      // Default error message
      let errorMessage = 'An unexpected error occurred during login. Please try again.';
      
      // Try to extract a more specific error message
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Log the full error for debugging
      console.error('[Login] Full error:', JSON.stringify(err, null, 2));
      
      // Show error to user
      toast.error(errorMessage);
      setErrors(prev => ({
        ...prev,
        general: errorMessage
      }));
      setLoading(false);
      clearTimeout(loginTimeout);
      
      return false;
    }
  };

  return (
    <Layout>
      <div className="login-page-container">
        <div className="login-split-screen">
          <div className="login-left-panel">
            <div className="login-form-container">
              <img src={logo} alt="UniTrade Logo" className="login-logo" />
              <h2>Welcome Back!</h2>
              <p className="login-subtitle">Login to continue to UniTrade</p>
              <div 
                className="error-container" 
                key={`error-${Date.now()}`}
                style={{
                  minHeight: '24px',
                  marginBottom: '1rem',
                  display: errors.general ? 'block' : 'none',
                  opacity: errors.general ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
                }}
              >
                {errors.general && (
                  <div className="error-message" role="alert" aria-live="assertive" style={{
                    display: 'block',
                    margin: '0 0 1rem 0',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fef2f2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem'
                  }}>
                    {errors.general}
                  </div>
                )}
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }} className="login-form" noValidate>
                <div className="input-group-login">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) {
                        setErrors(prev => ({ ...prev, email: '' }));
                      }
                    }}
                    className={errors.email ? 'input-error' : ''}
                    required
                  />
                  {errors.email && (
                    <div className="field-error" style={{
                      color: '#b91c1c',
                      fontSize: '0.75rem',
                      marginTop: '0.25rem',
                      marginLeft: '2.5rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#fef2f2',
                      borderRadius: '0.25rem',
                      display: 'block',
                      width: 'calc(100% - 2.5rem)'
                    }}>
                      {errors.email}
                    </div>
                  )}
                </div>
                <div className="input-group-login">
                  <FaLock className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors(prev => ({ ...prev, password: '' }));
                      }
                    }}
                    className={errors.password ? 'input-error' : ''}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  {errors.password && (
                    <div className="field-error" style={{
                      color: '#b91c1c',
                      fontSize: '0.75rem',
                      marginTop: '0.25rem',
                      marginLeft: '2.5rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#fef2f2',
                      borderRadius: '0.25rem',
                      display: 'block',
                      width: 'calc(100% - 2.5rem)'
                    }}>
                      {errors.password}
                    </div>
                  )}
                </div>
                <div className="remember-me-login">
                  <input type="checkbox" id="remember" />
                  <label htmlFor="remember">Remember me</label>
                </div>
                <div className="form-group">
                <button 
                  type="button" 
                  className="btn btn-primary w-100" 
                  disabled={loading}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit();
                  }}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
                <div className="text-center mt-3">
                  <Link to="/forgot-password" className="forgot-password-link">
                    Forgot your password?
                  </Link>
                </div>
              </div>  
                <p className="signup-link-login">
                  Don't have an account? <Link to="/register">Sign up</Link>
                </p>
              </form>
            </div>
          </div>
          <div className="login-right-panel">
            <div className="cta-content">
              <FaArrowRight className="cta-icon"/>
              <h2>New to UniTrade?</h2>
              <p>
                Join our community of students and professionals to buy and sell with ease.
                Signing up is quick and easy!
              </p>
              <Link to="/register" className="create-account-btn">
                CREATE AN ACCOUNT
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Login;